import { useState, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { patientQueryKeys, patientsApi } from "../../shared/api/patients";
import {
  encounterQueryKeys,
  encountersApi,
} from "../../shared/api/encounters";
import { prescriptionsApi } from "../../shared/api/prescriptions";
import { billingApi } from "../../shared/api/billing";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import {
  patientRelationQueryKeys,
  patientRelationsApi,
} from "../../shared/api/patientRelations";
import { serviceNamesOf } from "../../shared/types/appointment";
import { household, mergeByDate, type Household } from "./householdHistory";
import {
  ABNORMAL_FLAGS,
  FAMILY_RELATIONSHIPS,
  NOTE_TYPES,
  flagLabel,
  flagTone,
  medicalRecordsApi,
  medicalRecordsQueryKeys,
  noteTypeLabel,
  relationshipLabel,
  type LabResult,
} from "../../shared/api/medicalRecords";
import { apiError } from "../../shared/api/client";
import {
  appointmentStatusLabel,
  formatDate,
  formatDateTime,
  formatVnd,
} from "../../shared/format";
import { StatusPill } from "../../shared/ui/StatusPill";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { OperatingModule } from "../../shared/api/operatingMode";
import { useOperatedModules } from "../../shared/hooks/useOperatesModule";

type TabKey =
  | "appointments"
  | "encounters"
  | "prescriptions"
  | "invoices"
  | "allergies"
  | "surgeries"
  | "labs"
  | "family"
  | "notes";

/**
 * `module`: tab chỉ có nghĩa khi phòng khám dùng vùng đó trong Addy. Ba tab đầu đọc từ vùng Khám
 * bệnh, Đơn thuốc và Tài chính; phòng khám không dùng vùng nào thì tab đó ẩn đi thay vì mở ra một
 * lỗi 409/402.
 */
const TABS: { key: TabKey; label: string; module?: OperatingModule; history?: true }[] = [
  // Tab "Lịch sử khám" của salepro liệt kê PHIẾU (cả không đến / đã huỷ), không chỉ lần khám đã
  // hoàn thành — nên "Lịch hẹn" đứng đầu và không gác theo vùng: lịch hẹn có ở mọi gói.
  // `history`: bốn tab này chỉ có ở hồ sơ NGƯỜI ĐẶT; hồ sơ người thân dẫn sang đó.
  { key: "appointments", label: "Lịch hẹn", history: true },
  { key: "encounters", label: "Lần khám", module: "ENCOUNTERS", history: true },
  { key: "prescriptions", label: "Đơn thuốc & dịch vụ", module: "PRESCRIPTIONS", history: true },
  { key: "invoices", label: "Hóa đơn", module: "BILLING", history: true },
  { key: "allergies", label: "Dị ứng" },
  { key: "surgeries", label: "Phẫu thuật" },
  { key: "labs", label: "Kết quả xét nghiệm" },
  { key: "family", label: "Tiền sử gia đình" },
  { key: "notes", label: "Ghi chú" },
];

/**
 * Hồ sơ bệnh án — consolidated longitudinal patient record. Read-only tabs
 * (lịch hẹn / lần khám / đơn thuốc / hóa đơn / dị ứng) reuse existing list APIs; the four
 * editable tabs (phẫu thuật / xét nghiệm / tiền sử gia đình / ghi chú) are the
 * new patient medical-record endpoints with full add + soft-delete.
 *
 * <p>Bốn tab đọc đầu tiên gộp cả NGƯỜI THÂN đã liên kết của hồ sơ (xem `householdHistory.ts`):
 * tài khoản đặt lịch hộ con thấy đủ lịch / lần khám / đơn / hoá đơn của con ở đây, mỗi dòng mang
 * tên người khám — đúng tab "Lịch sử khám" của salepro. Và chiều ngược (quyết định 03/11/2026):
 * hồ sơ ĐANG LÀ người thân của ai đó KHÔNG hiện bốn tab này — lịch sử xem ở hồ sơ người đặt, trang
 * này chỉ dẫn sang đó. Dữ liệu không đổi chỗ, chỉ chỗ xem đổi. Bốn tab sửa được (dị ứng, phẫu
 * thuật…) vẫn là của riêng hồ sơ đang xem: đó là bệnh án của một người.
 */
export function PatientHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const [picked, setPicked] = useState<TabKey | null>(null);
  const modules = useOperatedModules();
  // Chưa biết thì chưa vẽ tab có gác: vẽ là bắn request, và request đó là thứ sẽ bị từ chối.
  const uses = (module: OperatingModule) => modules.loaded && modules.operates(module);
  const patient = useQuery({
    queryKey: patientQueryKeys.detail(id ?? ""),
    queryFn: () => patientsApi.get(id ?? ""),
    enabled: !!id,
  });
  const relatives = useQuery({
    queryKey: patientRelationQueryKeys.list(id ?? ""),
    queryFn: () => patientRelationsApi.list(id ?? ""),
    enabled: !!id,
  });
  // Ai đặt hộ hồ sơ này. Có → lịch sử ở trang của họ, không ở đây. Chưa tải / lỗi → coi như không
  // có ai (vẽ như trước), vì ẩn nhầm lịch sử của một người tệ hơn hiện thừa một lần.
  const holders = useQuery({
    queryKey: patientRelationQueryKeys.holders(id ?? ""),
    queryFn: () => patientRelationsApi.holders(id ?? ""),
    enabled: !!id,
  });
  const heldBy = holders.data ?? [];
  // Chưa biết ai giữ thì chưa vẽ tab nào: vẽ "Lịch hẹn" rồi rút lại là một lần gọi API thừa và
  // một màn hình nháy (test ghim: trang người thân không gọi /appointments).
  const tabs = holders.isPending
    ? []
    : TABS.filter((t) => !t.module || uses(t.module)).filter(
        (t) => !t.history || heldBy.length === 0,
      );
  const tab = tabs.some((t) => t.key === picked) ? picked : tabs[0]?.key;
  const usesPrescriptions = uses("PRESCRIPTIONS");
  const hh = household(id ?? "", patient.data?.fullName, relatives.data);
  // Guard AFTER hooks so hook order is stable (Rules of Hooks / React #310).
  if (!id) return <Navigate to="/patients" replace />;
  const relativeNames = hh.ids.slice(1).map((rid) => hh.nameOf[rid]).filter(Boolean);

  return (
    <div>
      <PageHeader
        title="Hồ sơ bệnh án"
        description={
          patient.data
            ? hh.hasRelatives
              ? `Toàn bộ hồ sơ y tế của ${patient.data.fullName} và người thân: ${relativeNames.join(", ")}.`
              : `Toàn bộ hồ sơ y tế của ${patient.data.fullName}.`
            : "Toàn bộ hồ sơ y tế của bệnh nhân."
        }
        actions={
          <Button asChild variant="outline">
            <Link to={`/patients/${id}`}>
              <ArrowLeft />
              Hồ sơ bệnh nhân
            </Link>
          </Button>
        }
      />

      {heldBy.length > 0 && (
        <div
          className="mb-4 rounded-lg border border-dashed p-4 text-sm"
          data-testid="history-held-by"
        >
          Lịch hẹn, lần khám, đơn thuốc và hoá đơn của{" "}
          <strong>{patient.data?.fullName ?? "hồ sơ này"}</strong> nằm ở hồ sơ người đặt:{" "}
          {heldBy.map((h, i) => (
            <span key={h.relationId}>
              {i > 0 ? ", " : ""}
              <Link to={`/patients/${h.patientId}/history`} className="text-primary hover:underline">
                {h.fullName}
              </Link>
            </span>
          ))}
          .
        </div>
      )}

      <div
        className="mb-4 flex flex-wrap gap-1 border-b"
        role="tablist"
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setPicked(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "appointments" && <AppointmentsTab household={hh} />}
      {tab === "encounters" && (
        <EncountersTab household={hh} withPrescriptions={usesPrescriptions} />
      )}
      {tab === "prescriptions" && <PrescriptionsTab household={hh} />}
      {tab === "invoices" && <InvoicesTab household={hh} />}
      {tab === "allergies" && <AllergiesTab patientId={id} />}
      {tab === "surgeries" && <SurgeriesTab patientId={id} />}
      {tab === "labs" && <LabsTab patientId={id} />}
      {tab === "family" && <FamilyTab patientId={id} />}
      {tab === "notes" && <NotesTab patientId={id} />}
    </div>
  );
}

// ============================================================ read-only tabs

/** Một truy vấn danh sách cho MỖI hồ sơ trong hộ, cùng khoá với trang danh sách tương ứng. */
function useHouseholdLists<T extends { id: string }>(
  hh: Household,
  key: (patientId: string) => readonly unknown[],
  fetch: (patientId: string) => Promise<{ content: T[] }>,
  dateOf: (row: T) => string | null | undefined,
  enabled = true,
) {
  const results = useQueries({
    queries: hh.ids.map((pid) => ({
      queryKey: key(pid),
      queryFn: () => fetch(pid),
      enabled,
    })),
  });
  return {
    rows: mergeByDate(
      results.map((r) => r.data?.content),
      dateOf,
    ),
    loading: results.some((r) => r.isLoading),
    error: results.some((r) => r.isError),
  };
}

/** Cột "Người khám": chỉ khi hộ có người thân — hồ sơ một người không cần nhắc tên ở mỗi dòng. */
function personHead(hh: Household, head: string[]): string[] {
  return hh.hasRelatives ? [head[0], "Người khám", ...head.slice(1)] : head;
}

function PersonCell({ hh, patientId, name }: { hh: Household; patientId: string; name: string | null }) {
  if (!hh.hasRelatives) return null;
  return <TableCell>{name || hh.nameOf[patientId] || "—"}</TableCell>;
}

function AppointmentsTab({ household: hh }: { household: Household }) {
  const { rows, loading, error } = useHouseholdLists(
    hh,
    (pid) => appointmentQueryKeys.list({ patientId: pid }, 0),
    (pid) => appointmentsApi.list({ patientId: pid }, { page: 0, size: 50 }),
    (a) => a.startAt,
  );
  return (
    <TableShell
      loading={loading}
      error={error}
      empty={rows.length === 0}
      head={personHead(hh, ["Ngày giờ", "STT", "Dịch vụ", "Bác sĩ", "Trạng thái", ""])}
    >
      {rows.map((a) => (
        <TableRow key={a.id}>
          <TableCell>{formatDateTime(a.startAt)}</TableCell>
          <PersonCell hh={hh} patientId={a.patientId} name={a.patientName} />
          <TableCell>{a.roomNo ?? a.registrationNo ?? "—"}</TableCell>
          <TableCell>{serviceNamesOf(a).join(", ") || "—"}</TableCell>
          <TableCell>{a.doctorName || "—"}</TableCell>
          <TableCell>
            <StatusPill
              tone={
                a.status === "COMPLETED"
                  ? "success"
                  : a.status === "CANCELLED" || a.status === "NO_SHOW"
                    ? "danger"
                    : "warning"
              }
            >
              {appointmentStatusLabel[a.status] ?? a.status}
            </StatusPill>
          </TableCell>
          <TableCell>
            <Button asChild variant="outline" size="sm">
              <Link to={`/appointments/${a.id}`}>Xem</Link>
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableShell>
  );
}

function EncountersTab({
  household: hh,
  withPrescriptions,
}: {
  household: Household;
  /** Có nối mỗi lần khám với đơn thuốc của nó không — chỉ khi phòng khám dùng vùng Đơn thuốc. */
  withPrescriptions: boolean;
}) {
  const { rows, loading, error } = useHouseholdLists(
    hh,
    (pid) => encounterQueryKeys.list({ patientId: pid }, 0),
    (pid) => encountersApi.list({ patientId: pid }, { page: 0, size: 50 }),
    (e) => e.startedAt,
  );
  // Prescriptions for these patients, indexed by the encounter that produced them,
  // so each visit row can link straight to its prescription(s) (#18529).
  const rx = useHouseholdLists(
    hh,
    (pid) => ["prescriptions", "list", { patientId: pid }, 0],
    (pid) => prescriptionsApi.list({ patientId: pid }, { page: 0, size: 50 }),
    (p) => p.createdAt,
    withPrescriptions,
  );
  const rxByEncounter = new Map<string, typeof rx.rows>();
  for (const p of rx.rows) {
    if (!p.encounterId) continue;
    const bucket = rxByEncounter.get(p.encounterId) ?? [];
    bucket.push(p);
    rxByEncounter.set(p.encounterId, bucket);
  }
  return (
    <TableShell
      loading={loading}
      error={error}
      empty={rows.length === 0}
      head={personHead(hh, [
        "Ngày khám",
        "Bác sĩ",
        "Lý do khám",
        "Chẩn đoán",
        "Trạng thái",
        "Đơn thuốc",
        "",
      ])}
    >
      {rows.map((e) => {
        const linkedRx = rxByEncounter.get(e.id) ?? [];
        return (
          <TableRow key={e.id}>
            <TableCell>{formatDate(e.startedAt)}</TableCell>
            <PersonCell hh={hh} patientId={e.patientId} name={e.patientName} />
            <TableCell>{e.doctorName}</TableCell>
            <TableCell>{e.chiefComplaint ?? "—"}</TableCell>
            <TableCell>{e.diagnosisSummary ?? "—"}</TableCell>
            <TableCell>
              <StatusPill
                tone={
                  e.status === "SIGNED"
                    ? "success"
                    : e.status === "CANCELLED"
                      ? "danger"
                      : "warning"
                }
              >
                {e.status === "SIGNED"
                  ? "Hoàn thành"
                  : e.status === "CANCELLED"
                    ? "Đã hủy"
                    : "Đang thực hiện"}
              </StatusPill>
            </TableCell>
            <TableCell>
              {linkedRx.length === 0 ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span className="inline-flex flex-wrap gap-2">
                  {linkedRx.map((p, i) => (
                    <Link
                      key={p.id}
                      to={`/prescriptions/${p.id}`}
                      className="text-primary hover:underline"
                    >
                      {linkedRx.length > 1 ? `Đơn ${i + 1}` : "Xem đơn"}
                    </Link>
                  ))}
                </span>
              )}
            </TableCell>
            <TableCell>
              <Button asChild variant="outline" size="sm">
                <Link to={`/encounters/${e.id}`}>Xem</Link>
              </Button>
            </TableCell>
          </TableRow>
        );
      })}
    </TableShell>
  );
}

function PrescriptionsTab({ household: hh }: { household: Household }) {
  const { rows, loading, error } = useHouseholdLists(
    hh,
    (pid) => ["prescriptions", "list", { patientId: pid }, 0],
    (pid) => prescriptionsApi.list({ patientId: pid }, { page: 0, size: 50 }),
    (p) => p.createdAt,
  );
  return (
    <TableShell
      loading={loading}
      error={error}
      empty={rows.length === 0}
      head={personHead(hh, ["Ngày kê", "Bác sĩ", "Số thuốc", "Hiệu lực", "Trạng thái", ""])}
    >
      {rows.map((p) => (
        <TableRow key={p.id}>
          <TableCell>{formatDate(p.createdAt)}</TableCell>
          <PersonCell hh={hh} patientId={p.patientId} name={p.patientName} />
          <TableCell>{p.doctorName}</TableCell>
          <TableCell>{p.itemCount} thuốc</TableCell>
          <TableCell>
            {p.validityStart ? formatDate(p.validityStart) : "—"}
            {p.validityEnd ? ` → ${formatDate(p.validityEnd)}` : ""}
          </TableCell>
          <TableCell>
            <StatusPill tone={p.status === "SIGNED" ? "success" : "warning"}>
              {p.status}
            </StatusPill>
          </TableCell>
          <TableCell>
            <Button asChild variant="outline" size="sm">
              <Link to={`/prescriptions/${p.id}`}>Xem</Link>
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableShell>
  );
}

function InvoicesTab({ household: hh }: { household: Household }) {
  const { rows, loading, error } = useHouseholdLists(
    hh,
    (pid) => ["invoices", "list", { patientId: pid }, 0],
    (pid) => billingApi.list({ patientId: pid }, { page: 0, size: 50 }),
    (inv) => inv.issuedAt,
  );
  return (
    <TableShell
      loading={loading}
      error={error}
      empty={rows.length === 0}
      head={personHead(hh, ["Ngày", "Mã HĐ", "Tổng tiền", "Đã thu", "Còn nợ", "Trạng thái", ""])}
    >
      {rows.map((inv) => (
        <TableRow key={inv.id}>
          <TableCell>{formatDate(inv.issuedAt)}</TableCell>
          <PersonCell hh={hh} patientId={inv.patientId} name={inv.patientName} />
          <TableCell className="font-mono">{inv.code}</TableCell>
          <TableCell>{formatVnd(inv.grandTotal)}</TableCell>
          <TableCell>{formatVnd(inv.paidTotal)}</TableCell>
          <TableCell>{formatVnd(inv.balanceDue)}</TableCell>
          <TableCell>
            <StatusPill
              tone={
                inv.status === "PAID"
                  ? "success"
                  : inv.status === "VOIDED"
                    ? "danger"
                    : "warning"
              }
            >
              {inv.status}
            </StatusPill>
          </TableCell>
          <TableCell>
            <Button asChild variant="outline" size="sm">
              <Link to={`/invoices/${inv.id}`}>Xem</Link>
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableShell>
  );
}

function AllergiesTab({ patientId }: { patientId: string }) {
  const q = useQuery({
    queryKey: ["patient", patientId, "allergies"],
    queryFn: () => patientsApi.listAllergies(patientId),
  });
  const rows = q.data ?? [];
  return (
    <>
      <p className="mt-0 text-sm text-muted-foreground">
        Quản lý chi tiết dị ứng tại trang Hồ sơ bệnh nhân → tab Dị ứng.
      </p>
      {q.isError && (
        <p className="text-sm text-destructive">Không tải được dữ liệu.</p>
      )}
      {q.isLoading ? (
        <div className="rounded-md border bg-card py-8 text-center text-sm text-muted-foreground">
          Đang tải…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border bg-card py-8 text-center text-sm text-muted-foreground">
          Chưa có dữ liệu.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((a) => (
            <div key={a.id} className="rounded-lg border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-foreground">{a.substance}</span>
                <StatusPill
                  tone={
                    a.severity === "SEVERE" || a.severity === "ANAPHYLAXIS"
                      ? "danger"
                      : "warning"
                  }
                >
                  {a.severity}
                </StatusPill>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Nhóm: {a.category}
              </div>
              <div className="text-sm text-muted-foreground">
                Phản ứng: {a.reaction ?? "—"}
              </div>
              <div className="text-sm text-muted-foreground">
                Khởi phát: {a.onsetDate ? formatDate(a.onsetDate) : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================================================== shared shells

function TableShell({
  loading,
  error,
  empty,
  head,
  children,
}: {
  loading: boolean;
  error: boolean;
  empty: boolean;
  head: string[];
  children: ReactNode;
}) {
  return (
    <>
      {error && (
        <p className="text-sm text-destructive">Không tải được dữ liệu.</p>
      )}
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {head.map((h, i) => (
                <TableHead key={i}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={head.length}
                  className="py-8 text-center text-muted-foreground"
                >
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {!loading && empty && (
              <TableRow>
                <TableCell
                  colSpan={head.length}
                  className="py-8 text-center text-muted-foreground"
                >
                  Chưa có dữ liệu.
                </TableCell>
              </TableRow>
            )}
            {children}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

type FieldType =
  | "text"
  | "date"
  | "number"
  | "select"
  | "textarea"
  | "checkbox";
interface FieldCfg {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
}

/**
 * Generic add + soft-delete section for the four editable record types: list,
 * an expandable add form built from a field config, and a per-row "Ngưng"
 * (soft-delete) action.
 */
function CrudSection<T extends { id: string }>({
  patientId,
  queryKey,
  list,
  create,
  remove,
  fields,
  columns,
  addLabel,
}: {
  patientId: string;
  queryKey: readonly unknown[];
  list: (pid: string) => Promise<T[]>;
  create: (pid: string, body: Record<string, unknown>) => Promise<void>;
  remove: (pid: string, id: string) => Promise<void>;
  fields: FieldCfg[];
  columns: { header: string; render: (row: T) => ReactNode }[];
  addLabel: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({ queryKey, queryFn: () => list(patientId) });
  const rows = q.data ?? [];

  function refresh() {
    qc.invalidateQueries({ queryKey });
  }

  const save = useMutation({
    mutationFn: () => create(patientId, form),
    onSuccess: () => {
      setForm({});
      setOpen(false);
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const del = useMutation({
    mutationFn: (rid: string) => remove(patientId, rid),
    onSuccess: refresh,
  });

  return (
    <div>
      <div className="mb-3">
        <Button type="button" onClick={() => setOpen((o) => !o)}>
          {open ? "Đóng" : addLabel}
        </Button>
      </div>

      {open && (
        <form
          className="mb-4 grid gap-3 rounded-md border bg-card p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
            {fields.map((f) => (
              <label key={f.name} className="grid gap-1.5 text-sm">
                <span className="font-medium">
                  {f.label}
                  {f.required ? " *" : ""}
                </span>
                {f.type === "select" ? (
                  <select
                    value={(form[f.name] as string) ?? ""}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.name]: e.target.value }))
                    }
                    required={f.required}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">—</option>
                    {f.options?.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : f.type === "textarea" ? (
                  <Textarea
                    rows={2}
                    value={(form[f.name] as string) ?? ""}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.name]: e.target.value }))
                    }
                    className="min-h-0 resize-y"
                  />
                ) : f.type === "checkbox" ? (
                  <Checkbox
                    checked={Boolean(form[f.name])}
                    onCheckedChange={(v) =>
                      setForm((s) => ({ ...s, [f.name]: v === true }))
                    }
                  />
                ) : (
                  <Input
                    type={f.type}
                    value={(form[f.name] as string) ?? ""}
                    placeholder={f.placeholder}
                    required={f.required}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        [f.name]:
                          f.type === "number"
                            ? e.target.value === ""
                              ? null
                              : Number(e.target.value)
                            : e.target.value,
                      }))
                    }
                  />
                )}
              </label>
            ))}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Đang lưu…" : "Lưu"}
            </Button>
          </div>
        </form>
      )}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c, i) => (
                <TableHead key={i}>{c.header}</TableHead>
              ))}
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="py-8 text-center text-muted-foreground"
                >
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {!q.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="py-8 text-center text-muted-foreground"
                >
                  Chưa có dữ liệu.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id}>
                {columns.map((c, i) => (
                  <TableCell key={i}>{c.render(row)}</TableCell>
                ))}
                <TableCell>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (window.confirm("Ngưng sử dụng bản ghi này?"))
                        del.mutate(row.id);
                    }}
                  >
                    Ngưng
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ============================================================== editable tabs

function SurgeriesTab({ patientId }: { patientId: string }) {
  return (
    <CrudSection
      patientId={patientId}
      queryKey={medicalRecordsQueryKeys.surgeries(patientId)}
      list={medicalRecordsApi.surgeries.list}
      create={(pid, b) => medicalRecordsApi.surgeries.create(pid, b as never)}
      remove={medicalRecordsApi.surgeries.remove}
      addLabel="+ Thêm phẫu thuật"
      fields={[
        {
          name: "procedureName",
          label: "Tên phẫu thuật",
          type: "text",
          required: true,
          placeholder: "VD: Cắt ruột thừa",
        },
        { name: "surgeryDate", label: "Ngày phẫu thuật", type: "date" },
        { name: "hospital", label: "Bệnh viện", type: "text" },
        { name: "surgeon", label: "Phẫu thuật viên", type: "text" },
        { name: "notes", label: "Ghi chú", type: "textarea" },
      ]}
      columns={[
        {
          header: "Phẫu thuật",
          render: (r) => <strong>{r.procedureName}</strong>,
        },
        {
          header: "Ngày",
          render: (r) => (r.surgeryDate ? formatDate(r.surgeryDate) : "—"),
        },
        { header: "Bệnh viện", render: (r) => r.hospital ?? "—" },
        { header: "PTV", render: (r) => r.surgeon ?? "—" },
        { header: "Ghi chú", render: (r) => r.notes ?? "—" },
      ]}
    />
  );
}

function LabsTab({ patientId }: { patientId: string }) {
  return (
    <>
      <p className="mt-0 text-sm text-muted-foreground">
        Nhập tay kết quả xét nghiệm. Tích hợp máy/LIS tự động là tính năng gói
        Pro.
      </p>
      <p className="mt-0 text-sm text-muted-foreground">
        Bệnh nhân tự xem kết quả thường quy trên Mini App và website. Kết quả
        đánh dấu <b>nhạy cảm</b> (HIV, giải phẫu bệnh, di truyền, tiền sản…) và
        kết quả <b>Nguy hiểm</b> được giữ lại cho tới khi bác sĩ đã báo bệnh nhân
        và bấm “Cho bệnh nhân xem”.
      </p>
      <CrudSection
        patientId={patientId}
        queryKey={medicalRecordsQueryKeys.labResults(patientId)}
        list={medicalRecordsApi.labResults.list}
        create={(pid, b) =>
          medicalRecordsApi.labResults.create(pid, {
            ...b,
            sensitive: Boolean((b as Record<string, unknown>).sensitive),
          } as never)
        }
        remove={medicalRecordsApi.labResults.remove}
        addLabel="+ Thêm kết quả"
        fields={[
          {
            name: "testName",
            label: "Tên xét nghiệm",
            type: "text",
            required: true,
            placeholder: "VD: Glucose máu",
          },
          { name: "resultValue", label: "Kết quả", type: "text" },
          { name: "unit", label: "Đơn vị", type: "text" },
          {
            name: "referenceRange",
            label: "Khoảng tham chiếu",
            type: "text",
            placeholder: "3.9-5.6",
          },
          {
            name: "abnormalFlag",
            label: "Đánh giá",
            type: "select",
            options: ABNORMAL_FLAGS,
          },
          { name: "takenAt", label: "Ngày lấy mẫu", type: "date" },
          { name: "labName", label: "Phòng xét nghiệm", type: "text" },
          {
            name: "sensitive",
            label: "Nhạy cảm — không tự cho bệnh nhân xem",
            type: "checkbox",
          },
          { name: "notes", label: "Ghi chú (nội bộ)", type: "textarea" },
        ]}
        columns={[
          {
            header: "Xét nghiệm",
            render: (r) => <strong>{r.testName}</strong>,
          },
          {
            header: "Kết quả",
            render: (r) =>
              `${r.resultValue ?? "—"}${r.unit ? " " + r.unit : ""}`,
          },
          { header: "Tham chiếu", render: (r) => r.referenceRange ?? "—" },
          {
            header: "Đánh giá",
            render: (r) =>
              r.abnormalFlag ? (
                <StatusPill tone={flagTone(r.abnormalFlag) as never}>
                  {flagLabel(r.abnormalFlag)}
                </StatusPill>
              ) : (
                "—"
              ),
          },
          {
            header: "Ngày",
            render: (r) => (r.takenAt ? formatDate(r.takenAt) : "—"),
          },
          {
            header: "Bệnh nhân xem",
            render: (r) => <LabVisibilityCell patientId={patientId} row={r} />,
          },
        ]}
      />
    </>
  );
}

/**
 * Whether the patient sees this result on their own channels (#19137 Q6), and the switch. With no
 * staff decision the rule applies: routine results are open, sensitive and "Nguy hiểm" ones are
 * held until a doctor has told the patient and opens them here.
 */
function LabVisibilityCell({
  patientId,
  row,
}: {
  patientId: string;
  row: LabResult;
}) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (visible: boolean | null) =>
      medicalRecordsApi.labResults.setPatientVisibility(
        patientId,
        row.id,
        visible,
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: medicalRecordsQueryKeys.labResults(patientId),
      }),
  });
  // The API omits null fields, so an undecided row arrives with patientVisible undefined.
  const decided = row.patientVisible != null;
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {row.visibleToPatient ? (
          <StatusPill tone="success">Đang cho xem</StatusPill>
        ) : decided ? (
          <StatusPill tone="neutral">Đã ẩn</StatusPill>
        ) : (
          <StatusPill tone="warning">
            {row.abnormalFlag === "CRITICAL" && !row.sensitive
              ? "Giữ lại · kết quả nguy hiểm"
              : "Giữ lại"}
          </StatusPill>
        )}
        {row.sensitive && <StatusPill tone="danger">Nhạy cảm</StatusPill>}
      </div>
      <div className="flex flex-wrap gap-1">
        <Button
          size="sm"
          variant="secondary"
          disabled={set.isPending}
          onClick={() => set.mutate(!row.visibleToPatient)}
        >
          {row.visibleToPatient ? "Ẩn với bệnh nhân" : "Cho bệnh nhân xem"}
        </Button>
        {decided && (
          <Button
            size="sm"
            variant="ghost"
            disabled={set.isPending}
            onClick={() => set.mutate(null)}
          >
            Theo quy tắc
          </Button>
        )}
      </div>
      {set.isError && (
        <span className="text-xs text-destructive">
          {apiError(set.error).detail}
        </span>
      )}
    </div>
  );
}

function FamilyTab({ patientId }: { patientId: string }) {
  return (
    <CrudSection
      patientId={patientId}
      queryKey={medicalRecordsQueryKeys.familyHistory(patientId)}
      list={medicalRecordsApi.familyHistory.list}
      create={(pid, b) =>
        medicalRecordsApi.familyHistory.create(pid, {
          ...b,
          deceased: Boolean((b as Record<string, unknown>).deceased),
        } as never)
      }
      remove={medicalRecordsApi.familyHistory.remove}
      addLabel="+ Thêm tiền sử"
      fields={[
        {
          name: "relationship",
          label: "Quan hệ",
          type: "select",
          required: true,
          options: FAMILY_RELATIONSHIPS,
        },
        {
          name: "condition",
          label: "Bệnh lý",
          type: "text",
          required: true,
          placeholder: "VD: Tăng huyết áp",
        },
        { name: "ageAtOnset", label: "Tuổi khởi phát", type: "number" },
        { name: "deceased", label: "Đã mất", type: "checkbox" },
        { name: "notes", label: "Ghi chú", type: "textarea" },
      ]}
      columns={[
        {
          header: "Quan hệ",
          render: (r) => relationshipLabel(r.relationship),
        },
        { header: "Bệnh lý", render: (r) => <strong>{r.condition}</strong> },
        { header: "Tuổi khởi phát", render: (r) => r.ageAtOnset ?? "—" },
        {
          header: "Tình trạng",
          render: (r) =>
            r.deceased ? (
              <StatusPill tone="neutral">Đã mất</StatusPill>
            ) : (
              <StatusPill tone="success">Còn sống</StatusPill>
            ),
        },
        { header: "Ghi chú", render: (r) => r.notes ?? "—" },
      ]}
    />
  );
}

function NotesTab({ patientId }: { patientId: string }) {
  return (
    <CrudSection
      patientId={patientId}
      queryKey={medicalRecordsQueryKeys.notes(patientId)}
      list={medicalRecordsApi.notes.list}
      create={(pid, b) => medicalRecordsApi.notes.create(pid, b as never)}
      remove={medicalRecordsApi.notes.remove}
      addLabel="+ Thêm ghi chú"
      fields={[
        { name: "noteType", label: "Loại", type: "select", options: NOTE_TYPES },
        { name: "body", label: "Nội dung", type: "textarea", required: true },
      ]}
      columns={[
        { header: "Loại", render: (r) => noteTypeLabel(r.noteType) },
        { header: "Nội dung", render: (r) => r.body },
        { header: "Ngày tạo", render: (r) => formatDate(r.createdAt) },
      ]}
    />
  );
}
